/**
 * AJAX cart: add to cart, quantity changes, removal, and the slide-out drawer.
 *
 * Uses the Section Rendering API so the drawer and cart page markup are always
 * produced by Liquid rather than rebuilt in JS. Single-item requests retain a
 * normal-form fallback; multi-item requests keep the shopper's selections and
 * report failures inline.
 */
(() => {
  const DRAWER_SECTION = 'pt-cart-drawer';

  // The cart page's main section lives in a JSON template, so its id is
  // template-scoped (e.g. `template--123__main`). Liquid publishes it here.
  const cartSectionId = () => document.querySelector('[data-pt-cart-section]')?.dataset.ptCartSection;

  const drawer = () => document.querySelector('.pt-drawer[data-pt-cart-drawer]');
  const enabled = () => document.body.dataset.ptCartDrawerEnabled === 'true';
  const cartUrl = (root = window.Shopify?.routes?.root ?? '/') => `${root}cart`;
  const getCartCompletion = (drawerEnabled, root) =>
    drawerEnabled ? { action: 'drawer' } : { action: 'redirect', url: cartUrl(root) };
  const shouldFallbackToForm = (isMultiItemRequest) => !isMultiItemRequest;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { cartUrl, getCartCompletion, shouldFallbackToForm };
  }

  if (typeof document === 'undefined') return;

  let lastFocused = null;

  /* ------------------------------------------------------------------ drawer */

  const openDrawer = () => {
    const el = drawer();
    if (!el) return;

    lastFocused = document.activeElement;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    el.querySelector('.pt-drawer__panel')?.focus();
  };

  const closeDrawer = () => {
    const el = drawer();
    if (!el) return;

    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (lastFocused instanceof HTMLElement) lastFocused.focus();
  };

  const isOpen = () => drawer()?.classList.contains('is-open') ?? false;

  /* ------------------------------------------------------------------ render */

  const sectionsToRequest = () => {
    const wanted = [];
    if (document.getElementById(`shopify-section-${DRAWER_SECTION}`)) wanted.push(DRAWER_SECTION);

    const cartId = cartSectionId();
    if (cartId) wanted.push(cartId);
    return wanted;
  };

  const applySections = (sections) => {
    if (!sections) return;

    Object.entries(sections).forEach(([id, html]) => {
      const target = document.getElementById(`shopify-section-${id}`);
      if (!target || typeof html !== 'string') return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.getElementById(`shopify-section-${id}`);
      if (fresh) target.innerHTML = fresh.innerHTML;
    });
  };

  const updateCount = (count) => {
    document.querySelectorAll('[data-pt-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  };

  const refreshCount = async () => {
    try {
      const res = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`);
      if (res.ok) updateCount((await res.json()).item_count);
    } catch {
      /* count is cosmetic; ignore */
    }
  };

  const busy = (state) => {
    drawer()?.querySelector('.pt-drawer__body')?.classList.toggle('pt-cart--busy', state);

    const cartId = cartSectionId();
    if (cartId) {
      document.getElementById(`shopify-section-${cartId}`)?.classList.toggle('pt-cart--busy', state);
    }
  };

  /* --------------------------------------------------------------- add to cart */

  const addToCart = async (form, items) => {
    const submit = form.querySelector('[type="submit"]');
    if (submit?.getAttribute('aria-busy') === 'true') return;
    submit?.setAttribute('aria-busy', 'true');
    if (submit instanceof HTMLButtonElement) submit.disabled = true;

    const sectionIds = sectionsToRequest();
    const isMultiItemRequest = Boolean(items?.length);
    const status = form.querySelector('[data-pt-form-status]');
    if (status) {
      status.textContent = '';
      status.hidden = true;
    }

    try {
      let res;

      if (items?.length) {
        res = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: sectionIds, sections_url: window.location.pathname }),
        });
      } else {
        const body = new FormData(form);
        sectionIds.forEach((id) => body.append('sections[]', id));
        res = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body,
        });
      }
      const data = await res.json();

      if (!res.ok) {
        const message = data.description || data.message;
        const responseError = new Error(message);
        responseError.isShopifyResponse = Boolean(message);
        throw responseError;
      }

      const completion = getCartCompletion(enabled(), window.Shopify?.routes?.root ?? '/');
      if (completion.action === 'redirect') {
        window.location.assign(completion.url);
        return;
      }

      applySections(data.sections);
      await refreshCount();
      openDrawer();
    } catch (error) {
      console.error('[pt-cart] add to cart failed', error);
      if (!shouldFallbackToForm(isMultiItemRequest)) {
        if (status) {
          status.textContent = error.isShopifyResponse ? error.message : status.dataset.defaultError;
          status.hidden = false;
        }
      } else {
        // Single-item product forms keep the existing progressive fallback.
        form.submit();
      }
    } finally {
      submit?.removeAttribute('aria-busy');
      if (submit instanceof HTMLButtonElement && window.location.pathname !== cartUrl()) {
        submit.disabled = submit.dataset.ptBaseDisabled === 'true' || submit.dataset.ptBundleInvalid === 'true';
      }
    }
  };

  /* ----------------------------------------------------------------- line change */

  const changeLine = async (line, quantity) => {
    busy(true);
    const reopen = isOpen();

    try {
      const res = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: sectionsToRequest() }),
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data.description || 'Cart update failed');

      applySections(data.sections);
      updateCount(data.item_count);
      if (reopen) openDrawer();
    } catch (error) {
      console.error('[pt-cart] cart update failed, reloading', error);
      window.location.reload();
    } finally {
      busy(false);
    }
  };

  /* -------------------------------------------------------------------- events */

  document.addEventListener('pt:cart-add', (event) => {
    const { form, items } = event.detail ?? {};
    if (!(form instanceof HTMLFormElement) || !items?.length) return;
    addToCart(form, items);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!enabled()) return;

    // Only intercept product add-to-cart forms, never the cart or checkout forms.
    const isAddForm =
      form.action.includes('/cart/add') ||
      form.querySelector('input[name="id"], select[name="id"]') !== null;
    if (!isAddForm || form.dataset.ptCartBypass === 'true') return;

    event.preventDefault();
    addToCart(form);
  });

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-pt-cart-close]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    if (enabled() && event.target.closest('[data-pt-cart-open]')) {
      event.preventDefault();
      openDrawer();
      return;
    }

    const change = event.target.closest('[data-pt-cart-change]');
    if (change) {
      event.preventDefault();
      changeLine(Number(change.dataset.ptCartChange), Number(change.dataset.ptCartQty));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isOpen()) closeDrawer();
  });

  // Keep focus inside the panel while the drawer is open.
  document.addEventListener('focusin', (event) => {
    if (!isOpen()) return;
    const panel = drawer()?.querySelector('.pt-drawer__panel');
    if (panel && !panel.contains(event.target)) panel.focus();
  });
})();
